// =============================================================================
// DATA LOADER SERVICE
// =============================================================================
// Service for loading page data from various sources with caching

import type { ContentComponent, NavItem, SEOData } from '@/types';
import { dataCache } from './dataCache';
import { isFirebaseConfigured } from './firestore';
import { contentStorage } from './contentStorage';
import { addComponentOrder, sortComponentsByOrder } from '@/utils/serialization';

export type DataSource = 'cache' | 'local' | 'firebase' | 'published';

export interface PageDataBundle {
  pageId: string;
  components: ContentComponent[];
  navigation?: {
    navItems?: NavItem[];
    footerLinks?: NavItem[];
    socialLinks?: NavItem[];
  };
  seo?: SEOData;
  source: DataSource;
}

/**
 * Data loader service that can load from multiple sources with caching
 * Priority: Cache -> Firebase Storage (published) -> Local Files
 */
export class DataLoaderService {
  private preferredSource: DataSource = isFirebaseConfigured ? 'published' : 'local';
  private useCache: boolean = true;

  /**
   * Set the preferred data source
   */
  setPreferredSource(source: DataSource): void {
    this.preferredSource = source;
  }

  /**
   * Get the current preferred source
   */
  getPreferredSource(): DataSource {
    return this.preferredSource;
  }

  /**
   * Enable or disable caching
   */
  setUseCache(useCache: boolean): void {
    this.useCache = useCache;
  }

  /**
   * Clear cache for a specific page
   */
  clearCache(pageId: string): void {
    dataCache.remove(pageId);
  }

  /**
   * Clear all cached pages
   */
  clearAllCache(): void {
    dataCache.clearAll();
  }

  /**
   * Load page data from Firebase Storage (published version for live site)
   * Path: content/published/{pageId}.json
   */
  async loadFromPublished(pageId: string): Promise<PageDataBundle | null> {
    if (!isFirebaseConfigured) {
      console.debug('[DataLoader] Firebase not configured, using local fallback');
      return this.loadFromLocal(pageId);
    }

    try {
      console.log(`[DataLoader] Attempting to load ${pageId} from Firebase Storage...`);
      const pageData = await contentStorage.loadPage(pageId);
      
      if (!pageData) {
        console.debug(`[DataLoader] Page ${pageId} not found in Firebase Storage, using local fallback`);
        return this.loadFromLocal(pageId);
      }

      // Load navigation from local files (only stored in home page)
      let navigation: PageDataBundle['navigation'];
      if (pageId === 'home') {
        try {
          const homeModule = await import('@/data/homeContent');
          navigation = {
            navItems: homeModule.navItems,
            footerLinks: homeModule.footerLinks,
            socialLinks: homeModule.socialLinks,
          };
        } catch (error) {
          console.warn('[DataLoader] Could not load navigation from local files:', error);
        }
      }

      // Ensure components have order and are sorted
      const orderedComponents = sortComponentsByOrder(addComponentOrder(pageData.components));

      return {
        pageId: pageData.pageId,
        components: orderedComponents,
        navigation,
        seo: pageData.seo,
        source: 'published',
      };
    } catch (error) {
      console.warn('[DataLoader] Error loading from published, falling back to local:', error);
      return this.loadFromLocal(pageId);
    }
  }

  /**
   * Load page data from Firebase Storage (alias for loadFromPublished)
   */
  async loadFromFirebase(pageId: string): Promise<PageDataBundle | null> {
    return this.loadFromPublished(pageId);
  }

  /**
   * Load page data from local TypeScript files (legacy)
   */
  async loadFromLocal(pageId: string): Promise<PageDataBundle | null> {
    try {
      let components: ContentComponent[] = [];
      let navigation: PageDataBundle['navigation'];
      
      switch (pageId) {
        case 'home': {
          const homeModule = await import('@/data/homeContent');
          components = homeModule.homePageComponents || [];
          navigation = {
            navItems: homeModule.navItems,
            footerLinks: homeModule.footerLinks,
            socialLinks: homeModule.socialLinks,
          };
          break;
        }
        case 'about': {
          const aboutModule = await import('@/data/aboutPageContent');
          components = aboutModule.aboutPageComponents || [];
          break;
        }
        case 'services': {
          const servicesModule = await import('@/data/servicesPageContent');
          components = servicesModule.servicesPageComponents || [];
          break;
        }
        case 'coaching': {
          const coachingModule = await import('@/data/coachingPageContent');
          components = coachingModule.coachingPageComponents || [];
          break;
        }
        case 'contact': {
          const contactModule = await import('@/data/contactPageContent');
          components = contactModule.contactPageComponents || [];
          break;
        }
        case 'portfolio': {
          const portfolioModule = await import('@/data/portfolioContent');
          components = portfolioModule.portfolioPageComponents || [];
          break;
        }
        case 'terms':
        case 'privacy': {
          const legalModule = await import('@/data/legalPageContent');
          components = pageId === 'terms' 
            ? legalModule.termsPageComponents || []
            : legalModule.privacyPageComponents || [];
          break;
        }
        default:
          console.warn(`Unknown page ID: ${pageId}`);
          return null;
      }

      // Ensure components have order and are sorted
      const orderedComponents = sortComponentsByOrder(addComponentOrder(components));

      return {
        pageId,
        components: orderedComponents,
        navigation,
        source: 'local',
      };
    } catch (error) {
      console.error('Error loading from local:', error);
      return null;
    }
  }

  /**
   * Load page data with caching and smart source selection
   * Priority: Cache -> Firebase Storage (published) -> Local Files
   */
  async loadPage(pageId: string): Promise<PageDataBundle | null> {
    try {
      // 1. Check cache first (if enabled)
      if (this.useCache) {
        const cached = dataCache.get(pageId);
        if (cached) {
          console.debug(`[DataLoader] Using cached data for ${pageId} (source: ${cached.source})`);
          return cached;
        }
      }

      // 2. Try loading from preferred source
      let result: PageDataBundle | null = null;
      
      switch (this.preferredSource) {
        case 'published':
        case 'firebase':
          result = await this.loadFromPublished(pageId);
          break;
        case 'local':
          result = await this.loadFromLocal(pageId);
          break;
        case 'cache':
          // Already checked cache above, fall through to firebase
          result = await this.loadFromPublished(pageId);
          break;
      }

      if (result) {
        // Cache the result
        if (this.useCache) {
          dataCache.set(pageId, result);
        }
        console.log(`[DataLoader] Successfully loaded ${pageId} from ${result.source}`);
        return result;
      }

      console.warn(`[DataLoader] Could not load page: ${pageId}`);
      return null;
    } catch (error) {
      console.error('[DataLoader] Error loading page:', error);
      return null;
    }
  }

  /**
   * Preload multiple pages into cache
   */
  async preloadPages(pageIds: string[]): Promise<void> {
    if (!this.useCache) {
      console.warn('Caching is disabled, skipping preload');
      return;
    }

    await dataCache.preload(pageIds, (pageId) => this.loadPageWithoutCache(pageId));
  }

  /**
   * Load page without using cache (for preloading and cache refresh)
   */
  private async loadPageWithoutCache(pageId: string): Promise<PageDataBundle | null> {
    const wasUsingCache = this.useCache;
    this.useCache = false;
    
    try {
      return await this.loadPage(pageId);
    } finally {
      this.useCache = wasUsingCache;
    }
  }

  // Removed admin migration methods as site is now public-only
}

// Export singleton instance
export const dataLoader = new DataLoaderService();

// Export class for custom instances
export default DataLoaderService;

